import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Trans, useTranslation } from 'react-i18next';
import {
  daysSince,
  extraSignGlyph,
  formatTargetMetricValue,
  muscleKey,
  type TodayAnchor,
  type TodayProposalView,
  type UnitsPreference,
  formatDayMonth,
} from '@heraion/shared';

import { formatRelativeAge } from '../formatRelativeAge';
import { Icon } from '../Icon/Icon';
import { painPartsPhrase } from '../bodymap/painPartsPhrase';
import { Button } from '../primitives/Button';
import { IconButton } from '../primitives/IconButton';
import { TextField } from '../primitives/TextField';
import { unitWord, weightUnitLabels } from '../weightUnitLabels';
import type { LoggerDispatch } from './state/loggerDraft';
import { rowIdOf, type DraftEntry } from './state/loggerDraftTypes';
import { TodayDraftEmpty } from './TodayDraftEmpty';
import { TodayDraftHeader, type TodayDraftControls } from './TodayDraftHeader';
import { TodayDraftLeftOut } from './TodayDraftLeftOut';
import { TodayLoadHint, todayNoAnchorText } from './TodayLoadHint';
import type { Theme } from './theme';
import { useDateLocale } from '../primitives/DateFormatContext';

// AP-9b: the unstarted «today» draft, in the slot EmptyFocusCard holds while
// no exercise is focused (mockup `mockup-today`, screen 4). Every exercise
// with the gap it closes, its sets, and reps and kg from the athlete's last
// time, editable here. Nothing is saved until Start: this card only reads the
// draft and writes it through the logger's own actions.
// AP-9c: the time chips on top and a shuffle per row and for all rows. What
// they do lives in useTodayDraft; the card only calls them. The header sits in
// its own box (TodayDraftHeader), the rows in a second one below it.
// P0 57d74690: under the reps and kg, where they came from (D1, D2).

export type { TodayDraftControls } from './TodayDraftHeader';

type Props = {
  proposal: TodayProposalView;
  entries: DraftEntry[];
  dispatch: LoggerDispatch;
  resolveName: (slug: string, snapshot?: string) => string;
  units: UnitsPreference;
  controls?: TodayDraftControls;
  onStart: () => void;
  onDiscard: () => void;
  /** Absent: the veto line has no link. */
  onOpenPainMap?: () => void;
  /** The empty card's exits. Absent: no button (a mount without the host). */
  onBuildAnyway?: () => void;
  onOpenCreateHub?: () => void;
  /** A «Build one anyway» is running. */
  building?: boolean;
  /** No muscle is under its weekly minimum (host reads the coverage). */
  weeklyMinimumMet?: boolean;
  theme: Theme;
};

export function TodayDraftCard({
  proposal,
  entries,
  dispatch,
  resolveName,
  units,
  controls,
  onStart,
  onDiscard,
  onOpenPainMap,
  onBuildAnyway,
  onOpenCreateHub,
  building = false,
  weeklyMinimumMet = false,
  theme,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dateLocale = useDateLocale();
  const styles = makeStyles(theme);
  const labels = weightUnitLabels(t);
  const fmtSets = (n: number) =>
    formatTargetMetricValue(n, 'hard-sets', units, locale, labels);
  const totalSets = entries.reduce((sum, e) => sum + e.sets.length, 0);

  // One value per row, written to every set of the entry: the draft proposes
  // the same reps and kg for each set, as the last time did.
  const setAll = (entryIdx: number, key: 'reps' | 'weight', value: string) => {
    entries[entryIdx]?.sets.forEach((_, setIdx) =>
      dispatch({ type: 'update_set', entryIdx, setIdx, patch: { [key]: value } }),
    );
  };

  const painParts = proposal.painCauses.map((c) => ({
    slug: c.slug,
    side: c.side === 'both' ? undefined : c.side,
  }));
  const veto =
    proposal.vetoedMuscles.length > 0
      ? t('mobile.logger.draftToday.painVeto', {
          parts: painPartsPhrase(t, painParts, locale),
          muscles: proposal.vetoedMuscles.map((m) => t(muscleKey(m))).join(', '),
          defaultValue: 'Left out for {{parts}} (pain map): {{muscles}}.',
        })
      : null;

  // D1: the date and age of the anchor set. D2: the similar exercise.
  const anchorLine = (anchor: TodayAnchor | null, slug: string): string => {
    if (anchor === null) return todayNoAnchorText(t, proposal, slug);
    if (anchor.source.kind === 'similar') {
      return t('mobile.logger.draftToday.anchorSimilar', { name: resolveName(anchor.source.slug) });
    }
    return t('mobile.logger.draftToday.anchorOwn', {
      date: formatDayMonth(new Date(anchor.atMs), dateLocale),
      age: formatRelativeAge(t, daysSince(anchor.atMs, Date.now())),
    });
  };

  const canShuffle = (exerciseId: string) =>
    (proposal.rows.find((r) => r.exerciseId === exerciseId)?.alternatives?.length ?? 0) > 1;

  return (
    <View style={styles.stack}>
      <TodayDraftHeader
        {...(controls ? { controls } : {})}
        exerciseCount={entries.length}
        totalSets={totalSets}
        restHours={proposal.restHours}
        ignoreRest={proposal.ignoreRest}
        hasMavRows={proposal.rows.some((r) => r.target === 'mav' && r.sets > 0)}
        canShuffleAll={entries.some((e) => canShuffle(e.exerciseId))}
        theme={theme}
      />
      <TodayLoadHint t={t} proposal={proposal} theme={theme} />

      {entries.length > 0 ? (
        <View style={styles.rows}>
          {entries.map((entry, entryIdx) => {
            const name = resolveName(entry.exerciseId, entry.exerciseName);
            const row = proposal.rows.find((r) => r.exerciseId === entry.exerciseId);
            const first = entry.sets[0];
            const alt = row?.alternatives?.find((a) => a.exerciseId === row.exerciseId);
            // LEGACY (TodayAlternative.last): no anchor, the old line.
            const anchor = alt?.anchor;
            const age =
              row && anchor === undefined && !row.hasHistory
                ? t('mobile.logger.draftToday.noHistory')
                : row && row.lastTrainedDays !== null
                  ? t('mobile.logger.draftToday.lastTrained', {
                      age: formatRelativeAge(t, row.lastTrainedDays),
                    })
                  : null;
            return (
              <View key={rowIdOf(entry)} style={[styles.row, entryIdx > 0 && styles.rowRule]}>
                <View style={styles.rowHead}>
                  <Text style={styles.name}>{name}</Text>
                  {controls && row && canShuffle(entry.exerciseId) ? (
                    <IconButton
                      theme={theme}
                      name="repeat"
                      onPress={() => controls.onShuffleRow(entry.exerciseId)}
                      accessibilityLabel={t('mobile.logger.draftToday.shuffleRowA11y', {
                        muscle: t(muscleKey(row.muscle)),
                      })}
                    />
                  ) : null}
                </View>
                {row ? (
                  <Text style={styles.small}>
                    <Trans
                      t={t}
                      i18nKey={
                        row.target === 'mav'
                          ? 'mobile.logger.draftToday.reasonMav'
                          : 'mobile.logger.draftToday.reason'
                      }
                      count={row.gap}
                      values={{ muscle: t(muscleKey(row.muscle)), n: fmtSets(row.gap) }}
                      components={{ b: <Text style={styles.strong} /> }}
                    />
                    {age ? ` · ${age}` : ''}
                  </Text>
                ) : null}
                <View style={styles.rx}>
                  <Text style={styles.mono}>{entry.sets.length}</Text>
                  <Text style={styles.small}>×</Text>
                  {first && !entry.isTimeBased && !entry.isCardio ? (
                    <>
                      <TextField
                        theme={theme}
                        numeric
                        touchFloor
                        value={first.reps}
                        placeholder="–"
                        onChangeText={(v) => setAll(entryIdx, 'reps', v)}
                        accessibilityLabel={`${name}, ${t('mobile.logger.draftToday.reps')}`}
                        style={[styles.field, first.reps === '' && styles.fieldEmpty]}
                      />
                      <Text style={styles.small}>{t('mobile.logger.draftToday.reps')}</Text>
                      {entry.isBodyweight ? (
                        // No load to propose on a bodyweight lift: named, not a
                        // dashed empty box that reads as something missing. The
                        // extra of the last time rides beside it (AP-9c).
                        <>
                          <Text style={styles.small}>{t('catalog.equipment_bodyweight')}</Text>
                          {first.weight !== '' ? (
                            <>
                              <Text style={styles.mono}>{extraSignGlyph(first.extraNegative)}</Text>
                              <TextField
                                theme={theme}
                                numeric
                                touchFloor
                                value={first.weight}
                                onChangeText={(v) => setAll(entryIdx, 'weight', v)}
                                accessibilityLabel={`${name}, ${t('mobile.logger.setExtra')}`}
                                style={styles.field}
                              />
                              <Text style={styles.small}>{unitWord(t, first.unit)}</Text>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <TextField
                            theme={theme}
                            numeric
                            touchFloor
                            value={first.weight}
                            placeholder="–"
                            onChangeText={(v) => setAll(entryIdx, 'weight', v)}
                            accessibilityLabel={`${name}, ${unitWord(t, first.unit)}`}
                            style={[styles.field, first.weight === '' && styles.fieldEmpty]}
                          />
                          <Text style={styles.small}>{unitWord(t, first.unit)}</Text>
                        </>
                      )}
                    </>
                  ) : null}
                </View>
                {anchor !== undefined ? <Text style={styles.small}>{anchorLine(anchor, entry.exerciseId)}</Text> : null}
                {first && entry.sets.length > 1 && !entry.isTimeBased && !entry.isCardio ? (
                  <Text style={styles.small}>
                    {t('mobile.logger.draftToday.allSets', {
                      count: entry.sets.length,
                      n: entry.sets.length,
                    })}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </View>
      ) : null}
      <TodayDraftLeftOut proposal={proposal} style={styles.small} />

      {veto ? (
        // Advisory, never red: the pain map informs, it does not alarm.
        <View style={styles.veto}>
          <Icon name="info" size={theme.typography.sizes.md} color={theme.palette.ink2} />
          <View style={styles.vetoBody}>
            <Text style={styles.vetoText}>{veto}</Text>
            {onOpenPainMap ? (
              <Pressable
                accessibilityRole="link"
                onPress={onOpenPainMap}
                style={({ pressed }) => [styles.link, pressed && styles.pressed]}
              >
                <Text style={styles.linkText}>{`${t('bodymap.painTitle')} ›`}</Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      ) : null}

      {entries.length > 0 ? (
        <>
          <Text style={styles.small}>{t('mobile.logger.draftToday.source')}</Text>
          <Button theme={theme} label={t('mobile.home.startToday')} onPress={onStart} full />
        </>
      ) : null}
      {entries.length === 0 ? (
        <TodayDraftEmpty
          proposal={proposal}
          weeklyMinimumMet={weeklyMinimumMet}
          building={building}
          {...(onBuildAnyway ? { onBuildAnyway } : {})}
          {...(onOpenCreateHub ? { onOpenCreateHub } : {})}
          theme={theme}
        />
      ) : null}
      <Button
        theme={theme}
        variant="ghost"
        label={t('mobile.logger.draftToday.discard')}
        onPress={onDiscard}
        full
      />
    </View>
  );
}

function makeStyles(theme: Theme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    stack: { gap: s.s4 },
    // Same box as EmptyFocusCard, solid instead of dashed: this one holds work.
    rows: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s5,
      gap: s.s4,
    },
    small: { color: p.ink2, fontSize: ty.sizes.sm, lineHeight: ty.sizes.sm * 1.4 },
    strong: { color: p.ink, fontWeight: '700' },
    row: { gap: s.s2 },
    rowRule: { borderTopWidth: 1, borderTopColor: p.border, paddingTop: s.s4 },
    rowHead: { flexDirection: 'row', alignItems: 'center', gap: s.s3 },
    name: { flex: 1, color: p.ink, fontSize: ty.sizes.base, fontWeight: '600' },
    rx: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: s.s3 },
    mono: { color: p.ink, fontFamily: ty.fonts.mono, fontSize: ty.sizes.base, fontWeight: '600' },
    field: { width: 64, fontFamily: ty.fonts.mono },
    // Empty is dashed AND shows «–»: never told by colour alone.
    fieldEmpty: { borderStyle: 'dashed' },
    veto: {
      flexDirection: 'row',
      gap: s.s3,
      alignItems: 'flex-start',
      backgroundColor: p.surface2,
      borderRadius: r.r3,
      padding: s.s4,
    },
    vetoBody: { flex: 1 },
    // The one safety line on the card: body size, not the footnote size.
    vetoText: { color: p.ink2, fontSize: ty.sizes.base, lineHeight: ty.sizes.base * 1.4 },
    link: { minHeight: 44, justifyContent: 'center', alignSelf: 'flex-start' },
    linkText: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: '600',
      textDecorationLine: 'underline',
    },
    pressed: { opacity: 0.6 },
  });
}
